import { observer } from 'mobx-react';
import { Component } from 'react';

import { FinalVisualizationBuilder } from '../../../app/configurator/VisualizationBuilder';
import { ShoppingCartItemData } from '../../../app/data/model';
import { AsyncCommand } from '../../../app/shared/common';
import ActionButton from '../../../app/shared/components/ActionButton';
import SecondaryButton from '../../../app/shared/components/Button/SecondaryButton';
import TertiaryButton from '../../../app/shared/components/Button/TertiaryButton';
import ConfigurationImage from '../../../app/shared/components/ConfigurationImage';
import InputNumber from '../../../app/shared/components/InputNumber/InputNumber';
import { Panel } from '../../../app/shared/components/Panel/Panel';
import { NumberInput } from '../../../app/shared/Form';
import { Authorize } from '../../authentication/Authorize';
import { CartItemBodyDetailsTranslation } from '../../localization/SiteTranslation';
import { BuyerPolicy } from '../../Policy';
import CartItemActions from '../CartItemActions';
import css from './CartItem.css';
import { CartItemDetails } from './CartItemDetails/CartItemDetails';
import { CartItemContextCommand, CartItemState } from './CartItemState';

interface CartItemProps {
  state: CartItemState;
}

@observer
export class CartItem extends Component<CartItemProps> {
  render() {
    const { state } = this.props;

    return (
      <Panel
        className={css.cartItem}
        bodyClassName={css.cartItemBody}
        header={{
          heading: <CartItemHeading item={state.item} contextCommands={state.cartItemContextCommands} />,
          headingActionButtons: (
            <CartItemActions items={state.cartItemContextCommands} translation={state.translation.cartItemActions} />
          ),
          headerClassName: css.cartItemHeader,
        }}
      >
        <CartItemBodyDetails
          item={state.item}
          quantityUpdate={state.quantityUpdate}
          updateQuantityCommand={state.updateQuantityCommand}
          translation={state.translation.cartItemBodyDetails}
        />
      </Panel>
    );
  }
}

interface CartItemHeaderProps {
  item: ShoppingCartItemData;
  contextCommands: Array<CartItemContextCommand>;
}

const CartItemHeading = ({ item }: CartItemHeaderProps) => {
  return (
    <div>
      <span className={css.cartItemHeading}>
        <strong>{item.baseModel.brandName}</strong> {item.baseModel.shortName}
      </span>
    </div>
  );
};

interface CartItemBodyProps {
  item: ShoppingCartItemData;
  quantityUpdate: NumberInput;
  updateQuantityCommand: AsyncCommand;
  translation: CartItemBodyDetailsTranslation;
}

export const CartItemBodyDetails = ({
  item,
  quantityUpdate,
  updateQuantityCommand,
  translation,
}: CartItemBodyProps) => {
  const imgWidth = 140;
  const imgHeight = 140;

  return (
    <>
      <ConfigurationImage
        configurationCodeOrigin={item.configurationCodeOrigin}
        className={css.cartItemBodyImage}
        src={FinalVisualizationBuilder.buildUrl(item.code, undefined, undefined, imgWidth, imgHeight)}
        width={imgWidth}
        height={imgHeight}
        sharpeningFactor={1.5}
        scalingMode="fit"
      />
      <div className={css.cartItemBodyDetails}>
        <CartItemDetails
          code={item.code}
          systemMessage={item.systemMessage}
          listPrice={item.listPrice}
          salePrice={item.salePrice}
          className={css.cartItemDetails}
          propertyClassNames={{
            propClassName: css.cartItemBodyDetailsProp,
            labelClassName: css.cartItemBodyDetailsLabel,
            valueClassName: css.cartItemBodyDetailsValue,
          }}
          translation={translation.cartItemDetails}
        />
        <Authorize policy={BuyerPolicy}>
          <ItemUpdateQuantityInput
            quantity={item.quantity}
            quantityUpdate={quantityUpdate}
            updateQuantityCommand={updateQuantityCommand}
            translation={translation}
          />
        </Authorize>
      </div>
    </>
  );
};

interface ItemUpdateQuantityInputProps {
  quantity: number;
  quantityUpdate: NumberInput;
  updateQuantityCommand: AsyncCommand;
  translation: CartItemBodyDetailsTranslation;
}

export const ItemUpdateQuantityInput = observer(
  ({ quantity, quantityUpdate, updateQuantityCommand, translation }: ItemUpdateQuantityInputProps) => {
    const updateText = quantityUpdate.value > 0 ? translation.updateQuantityButton : translation.removeItemButton;

    return (
      <>
        <InputNumber
          className={css.cartItemQuantityInput}
          input={quantityUpdate}
          onChange={quantityUpdate.onChange}
          min={0}
        />
        {quantity !== quantityUpdate.value && (
          <div className={css.cartItemActionButtonsContainer}>
            <TertiaryButton className={css.cartItemActionButton} onClick={() => quantityUpdate.onChange(quantity)}>
              {translation.revertButton}
            </TertiaryButton>
            <ActionButton
              className={css.cartItemActionButton}
              buttonElement={SecondaryButton}
              command={updateQuantityCommand}
            >
              {updateText}
            </ActionButton>
          </div>
        )}
      </>
    );
  },
);
